import { Button, Form, Image, Input, Row } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { NavigateFunction, useNavigate } from "react-router-dom";
import styled from "styled-components";
import pac from "../../package.json";
import imageUrl from "../assets/images/ajex_logo.png";
import { useUserContextHook } from "../hooks/useUserContextHook";
import { ILogin, ILoginState } from "../model/LoginModel";
import { useLoginStore } from "../stores/LoginStore";

import {
  APP_MENU_MAP,
  FOUR_ZERO_ONE,
  INVALID_USER_NAME_OR_PASSWORD_ERROR,
  applicationTimer,
  isNotEmptyObject,
} from "../utils/app-common";

import { SessionStorage } from "../services/storage";
import { requiredValidation } from "../validation/validation";
import LoadingModalComponent from "./CommonComponent/LoadingModalComponent";
import { useSetNotificationTokenMutation } from "../useQuery/SetNotificationTokenQuery";
import { getFcmToken } from "../hooks/FCM/notification/fcm.service";
import { v4 as uuidv4 } from "uuid";
import { getNotificationStyle } from "../common/notification";
import { isAccess, NotificationEnabledRoles } from "../utils/access-list";
import { detectIncognito } from "detectincognitojs";

const LoginHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 100vh;
`;

const LoginParent = styled.div`
  height: auto;
  width: 40%;
  border-style: solid;
  border-width: 0.1rem;
  border-top-color: rgb(249 115 22);
  border-top-width: 0.2rem;
  min-width: min-content;
  max-width: 28rem;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 1px 5px #c3c1c5;
`;

const ImageDiv = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LongInForm = styled.div`
  padding: 1.5rem;
  height: 15rem;
`;

const initialValues: ILogin = { username: "", password: "" };

export const LoginComponent = () => {
  const [form] = Form.useForm();
  const loginFormRef: React.MutableRefObject<any> = useRef(null);
  const { promiseInProgress } = usePromiseTracker({ area: "login-area" });
  const { promiseInProgress: initialAreaProgress } = usePromiseTracker({
    area: "initial-area",
  });
  const { mutateAsync, isPending } = useSetNotificationTokenMutation();
  const navigate: NavigateFunction = useNavigate();
  const [message, setMessage] = useState("");
  const [isIncognito, setIsIncognito] = useState(false);
  const hasSetNotificationTokenRef = useRef(false);

  const loginAction = useLoginStore(
    (state: ILoginState) => state?.PostLoginAction
  );
  const loggedInDetails = useLoginStore(
    (state: ILoginState) => state.loggedInDetails
  );
  const [isLoading, setIsLoading] = useState(false);
  console.log({ promiseInProgress, initialAreaProgress, isPending, isLoading });

  const { setEmployeeDetailHandle, employeeDetail, notificationToken, setNotificationToken } =
    useUserContextHook();

  const handleSubmit: (data: ILogin) => void = useCallback((data: ILogin) => {
    if (promiseInProgress || isPending || isLoading) return;
    hasSetNotificationTokenRef.current = false; // Allow fresh notification token attempt on each login
    trackPromise(loginAction(data), "login-area");
  }, [loginAction, promiseInProgress, isPending, isLoading]);

  const navigateUrl = useCallback((url: string) => navigate(url), [navigate]);

  // Move detectIncognito to useEffect to avoid running on every render
  useEffect(() => {
    detectIncognito().then((result) => {
      setIsIncognito(result.isPrivate);
    });
  }, []);



  useEffect(() => {
    if (isNotEmptyObject(loggedInDetails)) {
      if (loggedInDetails?.status === FOUR_ZERO_ONE) {
        setMessage(INVALID_USER_NAME_OR_PASSWORD_ERROR);
        return;
      }
      setEmployeeDetailHandle?.(form?.getFieldValue("username"));
    }
  }, [form, loggedInDetails, setEmployeeDetailHandle]);



  const handleSettingNotificationToken = useCallback(async () => {
    if (!employeeDetail || hasSetNotificationTokenRef.current) return;

    if (Notification.permission === "denied") {
      getNotificationStyle({
        status: 403,
        data: { message: "Please enable notifications to continue login" }
      });
      return;
    }

    hasSetNotificationTokenRef.current = true; // Set once — never reset to avoid retry loops
    setIsLoading(true);

    try {
      const token = await getFcmToken();

      if (!token) {
        getNotificationStyle({
          status: 403,
          data: { message: "Notifications must be enabled to login" }
        });
        setIsLoading(false);
        return;
      }

      const payload = {
        appVersion: "WEB",
        employeeId: employeeDetail.id,
        firebaseToken: token,
        uniqueId: uuidv4()
      };

      const res = await mutateAsync(payload);

      if (res?.status === 200 && res?.data?.statusCode === 200) {
        setNotificationToken?.(token);
      } else {
        getNotificationStyle(res);
      }
    } catch (error) {
      console.error("Error setting notification token", error);
      getNotificationStyle({
        status: 500,
        data: { message: "Unable to verify notification permission" }
      });
    } finally {
      setIsLoading(false);
    }
  }, [employeeDetail, mutateAsync, setNotificationToken]);



  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS || isIncognito || !isNotEmptyObject(employeeDetail)) return;

    const hasNotificationAccess = isAccess(NotificationEnabledRoles, employeeDetail?.positionId || 0);

    if (hasNotificationAccess && !notificationToken && !hasSetNotificationTokenRef.current) {
      handleSettingNotificationToken();
    }
  }, [employeeDetail, handleSettingNotificationToken, notificationToken, isIncognito]); // Optimize dependencies




  useEffect(() => {
    if (!isNotEmptyObject(employeeDetail)) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const hasNotificationAccess = isAccess(
      NotificationEnabledRoles,
      employeeDetail?.positionId || 0
    );

    // Wait for notification token if required
    if (hasNotificationAccess && !notificationToken && !isIOS && !isIncognito) {
      return;
    }

    SessionStorage.set("timer", `${applicationTimer}`);

    const redirectTo = SessionStorage.get("redirectTo");
    if (redirectTo && APP_MENU_MAP[redirectTo]) {
      SessionStorage.set("token", SessionStorage.get("token")!);
      SessionStorage.remove("redirectTo");
      window.location.replace(window.location.origin + redirectTo);
      return;
    }

    // Always redirect to AIMS Operation Welcome page - no complex routing logic
    navigateUrl("/aims-welcome");

  }, [
    employeeDetail,
    notificationToken,
    navigateUrl,
    isIncognito
  ]);

  return (
    <LoginHeader>
      <LoginParent>
        <LoadingModalComponent
          isLoading={promiseInProgress || initialAreaProgress || isPending || isLoading}
        >
          <>
            <ImageDiv>
              <Image
                src={imageUrl}
                alt="logo"
                width={250}
                preview={false}
              />
            </ImageDiv>
            <LongInForm>
              <Form
                form={form}
                ref={loginFormRef}
                initialValues={initialValues}
                onFinish={handleSubmit}
              >
                <Form.Item
                  name="username"
                  rules={[
                    {
                      ...requiredValidation,
                      message: "Please input your Username",
                    },
                  ]}
                >
                  <Input placeholder="Username" />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[
                    {
                      ...requiredValidation,
                      message: "Please input your Password",
                    },
                  ]}
                >
                  <Input.Password
                    visibilityToggle
                    placeholder="Password"
                    type="password"
                  />
                </Form.Item>
                <Row justify="center" align="middle">
                  <Button loading={isPending} block type="primary" htmlType="submit">
                    Login
                  </Button>
                </Row>
              </Form>
              <Row justify="center" align="middle" className="error-message">
                {message}
              </Row>
            </LongInForm>
          </>
        </LoadingModalComponent>
        <Row justify="center">Version: {pac.version}</Row>
      </LoginParent>
    </LoginHeader>
  );
};
